/**
 * Inline-formula node, in either of the two shapes inline math arrives in.
 *
 * Same reasoning as MathBlock: a rendered formula is a deep element tree that
 * ProseMirror would flatten to plain text, so the source is held as a node
 * attribute and rendered in a node view.
 *
 * - `code.math-inline` — what the Markdown pipeline emits for `$…$`, LaTeX source.
 * - `<math>` — MathML, which `html` content carries directly, most often inside
 *   table cells. Left unhandled it flattens to concatenated characters
 *   (`Lngini` + `it` reading as "Lnginiit"), so the cell looks corrupt.
 *
 * StarterKit's `code` mark is disabled in the viewer so it cannot claim the
 * `code.math-inline` element first.
 */
import { mergeAttributes, Node } from '@tiptap/core';
import katex from 'katex';

import { normalizeLatex } from './mathUtils';

/** Environments that need KaTeX's display layout to stack rows/columns at all. */
const NEEDS_DISPLAY_MODE =
  /\\begin\{(matrix|pmatrix|bmatrix|vmatrix|Vmatrix|array|cases|aligned|align)\}/;

export const InlineMath = Node.create({
  name: 'inlineMath',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: {
        default: '',
        parseHTML: (element: HTMLElement) =>
          element.tagName.toLowerCase() === 'math' ? '' : (element.textContent ?? ''),
        renderHTML: () => ({}),
      },
      /** Serialized MathML, kept verbatim; browsers render MathML natively. */
      mathml: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.tagName.toLowerCase() === 'math' ? element.outerHTML : null,
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'code.math-inline' }, { tag: 'math' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const mathml = node.attrs['mathml'];

    return [
      'code',
      mergeAttributes(HTMLAttributes, {
        class: 'math-inline',
        ...(typeof mathml === 'string' ? { 'data-mathml': mathml } : {}),
      }),
      String(node.attrs['latex'] ?? ''),
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const mathml = node.attrs['mathml'];
      const latex = String(node.attrs['latex'] ?? '');

      if (typeof mathml === 'string' && mathml) {
        // Already through DOMPurify: `renderContentValue` sanitizes every block's
        // content (MathML profile enabled) before the editor ever parses it.
        const dom = document.createElement('span');

        dom.className = 'math-inline';
        dom.setAttribute('data-part', 'mathml');
        dom.innerHTML = mathml;

        return { dom };
      }

      const dom = document.createElement('code');

      dom.className = 'math-inline';
      dom.setAttribute('data-math', latex);

      // The extraction service emits display formulas (matrices, arrays, cases)
      // as `$$...$$` on a single markdown line rather than on its own line, so
      // remark-math's flow-math check never fires and they arrive here as inline
      // math instead of reaching MathBlock. KaTeX's inline mode has no room to
      // stack an array/matrix's rows and columns, so those environments still
      // need `displayMode: true` even though this node is inline in the doc.
      const needsDisplayMode = NEEDS_DISPLAY_MODE.test(latex);

      if (needsDisplayMode) dom.classList.add('math-inline--display');

      try {
        katex.render(normalizeLatex(latex), dom, {
          displayMode: needsDisplayMode,
          throwOnError: false,
        });
      } catch {
        dom.textContent = latex;
      }

      return { dom };
    };
  },
});
